import {
    IconBanOutline,
    IconCheckCircleFilled,
    IconExclamationTriangleFilled
} from '@iwoca/icons';
import { AlertBox } from '@iwoca/orion';
import { isLinkingOpenBankingOnly } from '@iwRouter/LocationHelper';
import cn from 'classnames';
import { FormattedMessage } from 'react-intl';

import { AlertStatusTypes } from './TrueLayer';
import {
    TRUELAYER_CANCEL,
    TRUELAYER_FAILURE,
    TRUELAYER_PROVIDER_MIGRATED,
    TRUELAYER_SUCCESS
} from './truelayer.constants';
import styles from './trueLayer.module.css';

// Placeholder copy pending design.
const MigratedProviderContent = ({
    successorName,
    onReconnect
}: {
    successorName?: string;
    onReconnect: () => void;
}) => (
    <>
        <span className={styles.bold}>
            {successorName ? (
                <FormattedMessage
                    id="_.true_layer.provider_migrated"
                    defaultMessage="Your bank has moved to {successor}."
                    values={{ successor: successorName }}
                />
            ) : (
                <FormattedMessage
                    id="_.true_layer.provider_migrated_unknown"
                    defaultMessage="Your bank has moved to a new provider."
                />
            )}
        </span>{' '}
        <span className={styles.light}>
            {successorName ? (
                <FormattedMessage
                    id="_.true_layer.provider_migrated_action"
                    defaultMessage="Reconnect with {successor} to link your account."
                    values={{ successor: successorName }}
                />
            ) : (
                <FormattedMessage
                    id="_.true_layer.provider_migrated_action_unknown"
                    defaultMessage="Reconnect to link your account."
                />
            )}{' '}
            <button
                type="button"
                data-testid="reconnectViaSuccessor"
                onClick={onReconnect}
                className={styles.link}
            >
                <FormattedMessage
                    id="_.true_layer.provider_migrated_button"
                    defaultMessage="Connect account"
                />
            </button>
        </span>
    </>
);

export const TruelayerAlertBox = ({
    status,
    handleFailureButtonClick,
    handleCancelButtonClick,
    handleReconnectClick,
    successorName
}: {
    status: AlertStatusTypes;
    handleFailureButtonClick: () => void;
    handleCancelButtonClick: () => void;
    handleReconnectClick: () => void;
    successorName?: string;
}) => {
    const showUploadBankStatementsButton = !isLinkingOpenBankingOnly();

    if (status === TRUELAYER_SUCCESS) {
        return (
            <AlertBox
                variant={status}
                className={styles.alertBox}
                data-testid="alertbox"
            >
                <IconCheckCircleFilled
                    width={20}
                    height={20}
                    className={styles.successAlertBoxIcon}
                />
                <span
                    className={cn(
                        styles.alertBoxContent,
                        styles.successAlertBoxTextContent
                    )}
                >
                    <FormattedMessage
                        id="_.true_layer.linked"
                        defaultMessage="Great – you've linked your bank account."
                    />
                </span>
            </AlertBox>
        );
    }

    if (status === TRUELAYER_CANCEL) {
        return (
            <AlertBox
                variant={status}
                className={styles.alertBox}
                data-testid="alertbox"
            >
                <IconBanOutline
                    width={20}
                    height={20}
                    className={styles.cancelAlertBoxIcon}
                />
                <span
                    className={cn(
                        styles.alertBoxContent,
                        styles.cancelAlertBoxTextContent
                    )}
                >
                    <span className={styles.bold}>
                        <FormattedMessage
                            id="_.true_layer.no_permission"
                            defaultMessage="We didn't get your permission."
                        />
                    </span>{' '}
                    {showUploadBankStatementsButton && (
                        <button
                            type="button"
                            data-testid="uploadBankStatements"
                            onClick={handleCancelButtonClick}
                            className={styles.link}
                        >
                            <FormattedMessage
                                id="_.true_layer.no_permission_button"
                                defaultMessage="Upload your bank statements instead?"
                            />
                        </button>
                    )}
                </span>
            </AlertBox>
        );
    }

    return (
        <AlertBox
            variant={TRUELAYER_FAILURE}
            className={styles.alertBox}
            data-testid="alertbox"
        >
            <IconExclamationTriangleFilled
                width={20}
                height={20}
                className={styles.failureAlertBoxIcon}
            />
            <span
                className={cn(
                    styles.alertBoxContent,
                    styles.cancelAlertBoxTextContent
                )}
            >
                {status === TRUELAYER_PROVIDER_MIGRATED ? (
                    <MigratedProviderContent
                        successorName={successorName}
                        onReconnect={handleReconnectClick}
                    />
                ) : (
                    <>
                        <span className={styles.bold}>
                            <FormattedMessage
                                id="_.true_layer.failed"
                                defaultMessage="Hmm, that didn't work."
                            />
                        </span>{' '}
                        <span className={styles.light}>
                            {showUploadBankStatementsButton && (
                                <FormattedMessage
                                    id="_.true_layer.try_again"
                                    defaultMessage="Try again or {button} instead."
                                    values={{
                                        button: (
                                            <button
                                                type="button"
                                                data-testid="uploadBankStatements"
                                                onClick={
                                                    handleFailureButtonClick
                                                }
                                                className={styles.link}
                                            >
                                                <FormattedMessage
                                                    id="_.true_layer.try_again_button"
                                                    defaultMessage="upload your bank statements"
                                                />
                                            </button>
                                        )
                                    }}
                                />
                            )}
                        </span>
                    </>
                )}
            </span>
        </AlertBox>
    );
};
